import { useState, useId } from 'react'
import { X, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { formatVnNumber } from './poWorkflow'

export interface RemainingLineItem {
  id: string
  itemName: string
  itemCode: string
  qty: number
  receivedQty: number
  remainingQty: number
  uom: string
}

export interface PoCloseModalProps {
  isOpen: boolean
  onClose: () => void
  poCode: string
  remainingLines: RemainingLineItem[]
  onConfirm: (reason: string) => Promise<void>
  loading?: boolean
}

export function PoCloseModal({
  isOpen,
  onClose,
  poCode,
  remainingLines,
  onConfirm,
  loading = false,
}: PoCloseModalProps) {
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const reasonId = useId()

  if (!isOpen) return null

  const totalOrdered = remainingLines.reduce((acc, l) => acc + l.qty, 0)
  const totalReceived = remainingLines.reduce((acc, l) => acc + l.receivedQty, 0)
  const totalRemaining = remainingLines.reduce((acc, l) => acc + l.remainingQty, 0)

  async function handleConfirm() {
    setError(null)
    try {
      await onConfirm(reason.trim())
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Có lỗi xảy ra khi đóng PO')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-xs">
      <div
        className="w-full max-w-lg rounded-xl border border-slate-200 bg-white p-6 shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="po-close-title"
      >
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2 font-semibold text-slate-800">
            <AlertCircle className="h-5 w-5 text-amber-600" />
            <span id="po-close-title">Đóng đơn mua hàng {poCode}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            aria-label="Đóng"
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {error ? (
          <div className="mt-3 rounded border border-red-200 bg-red-50 p-2.5 text-xs text-red-700">
            {error}
          </div>
        ) : null}

        <div className="mt-4 space-y-3 text-xs">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="text-xs font-medium text-slate-700 mb-2">
              Danh sách vật tư còn thiếu ({remainingLines.length} dòng):
            </div>
            <div className="max-h-48 overflow-y-auto divide-y divide-slate-200 text-xs">
              {remainingLines.map((l) => (
                <div key={l.id} className="py-2 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-800 truncate">{l.itemName}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-slate-700">
                      Đặt: {formatVnNumber(l.qty)} · Đã nhận: {formatVnNumber(l.receivedQty)} {l.uom}
                    </div>
                    <div className="font-semibold text-amber-700">
                      Còn thiếu: {formatVnNumber(l.remainingQty)} {l.uom}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {remainingLines.length > 1 && (
              <div className="mt-2 pt-2 border-t border-slate-300 flex justify-between font-semibold text-slate-800 text-xs">
                <span>Tổng cộng:</span>
                <span>
                  Đặt {formatVnNumber(totalOrdered)} · Nhận {formatVnNumber(totalReceived)} · Thiếu{' '}
                  <span className="text-amber-700">{formatVnNumber(totalRemaining)}</span>
                </span>
              </div>
            )}
          </div>

          <div className="p-2.5 rounded border border-amber-200 bg-amber-50 text-amber-900 text-xs leading-relaxed">
            Bạn có muốn hủy toàn bộ phần số lượng còn lại và đóng đơn mua hàng này không?
            Trạng thái PO sẽ chuyển thành <strong>Thiếu hàng — đã xử lý</strong> (HOÀN THÀNH).
          </div>

          <div>
            <label htmlFor={reasonId} className="block font-medium text-slate-700 mb-1">
              Lý do hủy phần còn lại (tùy chọn):
            </label>
            <input
              id={reasonId}
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="VD: NCC thông báo hết hàng / Không còn nhu cầu sản xuất..."
              disabled={loading}
              className="w-full rounded border border-[var(--color-border)] p-2 text-xs focus:border-[var(--color-primary)] focus:outline-none"
            />
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
            <Button variant="secondary" size="sm" onClick={onClose} disabled={loading}>
              Hủy
            </Button>
            <Button
              variant="danger"
              size="sm"
              disabled={loading}
              onClick={handleConfirm}
            >
              {loading ? 'Đang xử lý...' : 'Hủy phần còn lại & đóng PO'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
